import { MaybeRefOrGetter, toRef } from "@vueuse/core";
import { ref, computed, watch } from "vue";
import env from "@/env";
import * as d3 from "d3-dsv";

import { convertNullsToUndefined, getClient } from "./client";
import download from "downloadjs";
import { useRfp, useRfpLocation } from "./useRfp";
import { useToast } from "./useToast";
import {
  useCustomFormQuestions,
  CustomQuestionTypeCSVLabel,
  Question,
  DEFAULT_TEXTFIELD_CHAR_LIMIT,
  DEFAULT_TEXTAREA_CHAR_LIMIT,
} from "./useCustomForm";
import {
  ItemCostCategoryDict,
  TechCostCategoryDict,
  TechCostCategory,
} from "@/lib/useAllCash";
import { MilestoneTypeDict } from "@/lib/useTimeline";
import { FormSettings } from "@/lib/useForm";
import { tableValue } from "@/plugins/Formatting";
import { objectEntries } from "@/lib/objectEntries";

const { pushToast } = useToast();

/**
 * START auto-generated from OpenAPI spec
 */
type ProposalFormTypeAPI =
  | "carport_solar"
  | "groundmount_solar"
  | "rooftop_solar"
  | "custom_form"
  | "ppa"
  | "rooftop_lease"
  | "timeline"
  | "all_cash";

type ProposalFormShorthandAPI = {
  /**
   * A unique identifier for the foundation form link
   */
  id: string;
  proposal_form_type: ProposalFormTypeAPI;
  /**
   * Name of the custom or foundation form
   */
  label: string;
  /**
   * Name of the provider linked to a proposal specific form
   */
  provider_name?: string;
  /**
   * ID of the proposal linked to a proposal specific form
   */
  proposal_id?: string;
  /**
   * The settings used to configure a foundation form
   */
  settings?: FormSettings;
};

type ProposalFormSlotTypeAPI = "all_of" | "one_of" | "many_of" | "any_of";

type ProposalFormSlotResponseAPI = {
  /**
   * A unique identifier for this slot.
   */
  id: string;
  /**
   * The part this slot is associated with.
   */
  part_id: string;
  /**
   * The type of slot - all_of, one_of, many_of, any_of.
   */
  slot_type: ProposalFormSlotTypeAPI;
  /**
   * The label for this slot.
   */
  label: string;
  /**
   * The rank of this slot in the form to properly order it
   */
  slot_rank: number;
  forms: Array<ProposalFormShorthandAPI>;
};

type ProposalFormPartResponseAPI = {
  /**
   * A unique identifier for this part.
   */
  id: string;
  /**
   * The RFP this part is associated with.
   */
  rfp_id: string;
  /**
   * The proposal this part is associated with.
   */
  proposal_id: string;
  /**
   * The RFP location for this part - null/undefined = global/all locations.
   */
  location_id: string | undefined;
  slots: Array<ProposalFormSlotResponseAPI>;
};

/**
 * END auto-generated from OpenAPI spec
 */

export type ProposalFormType = ProposalFormTypeAPI;
export type FoundationForm = {
  id: string;
  type: Exclude<ProposalFormTypeAPI, "custom_form">;
  label: string;
  providerName?: string;
  proposalId?: string;
  settings?: FormSettings;
};

export type CustomForm = {
  id: string;
  type: "custom_form";
  label: string;
  providerName?: string;
  proposalId?: string;
};

export type AnyForm = FoundationForm | CustomForm;

export type FormUpdateRequest = {
  proposal_form_type: ProposalFormType;
  label: string;
  proposal_id?: string | null;
  settings?: FormSettings;
};

export type AnySlot = {
  id: string;
  type: ProposalFormSlotTypeAPI;
  label: string;
  forms: Array<AnyForm>;
};

export type ProposalPart = {
  id: string;
  // Location ID is null when the proposal part is general to the whole proposal and is non-null
  // when it can be linked to a specific project location
  //
  // NOTE: Location IDs are unique; any location ID or null value can appear at most once in a given
  // proposal form config
  locationId: string | undefined;
  // Slots are the different sets of logically-related forms
  //
  // NOTE: the order here determines the display order
  slots: Array<AnySlot>;
};

export type ProposalFormConfig = {
  // Parts are the different parts of a proposal form; some are specific to locations, while others
  // are more general to the whole proposal
  //
  // NOTE: the order here is not meaningful as the frontend will have separate logic for determining
  parts: Array<ProposalPart>;
};

function apiFormToClientForm(apiForm: ProposalFormShorthandAPI): AnyForm {
  return {
    id: apiForm.id,
    type: apiForm.proposal_form_type,
    label: apiForm.label,
    providerName: apiForm.provider_name,
    proposalId: apiForm.proposal_id,
    settings: apiForm.settings,
  };
}

function apiSlotToClientSlot(apiSlot: ProposalFormSlotResponseAPI): AnySlot {
  return {
    id: apiSlot.id,
    type: apiSlot.slot_type,
    label: apiSlot.label,
    forms: apiSlot.forms.map(apiFormToClientForm),
  };
}

function apiPartToClientPart(
  apiPart: ProposalFormPartResponseAPI,
): ProposalPart {
  return {
    id: apiPart.id,
    locationId: apiPart.location_id,
    slots: apiPart.slots
      .sort((a, b) => a.slot_rank - b.slot_rank)
      .map(apiSlotToClientSlot),
  };
}

export function useProposalFormConfig(
  rfpId: MaybeRefOrGetter<string | undefined>,
  proposalId?: MaybeRefOrGetter<string | null>,
) {
  const reactiveRfpId = toRef(rfpId);
  const reactiveProposalId = toRef(proposalId);
  const loading = ref(true);
  const error = ref<any>(null);
  const proposalFormConfig = ref<ProposalFormConfig | undefined>();

  const client = getClient();

  async function fetch() {
    if (!reactiveRfpId.value) {
      proposalFormConfig.value = undefined;
      return;
    }

    loading.value = true;
    try {
      const apiResponse = await client
        .get<ProposalFormPartResponseAPI[]>(
          `/rfps/${reactiveRfpId.value}/proposal_form_parts`,
          {
            params: {
              // NOTE: We need to use undefined for previews
              specific_proposal_id: reactiveProposalId.value || undefined,
            },
          },
        )
        .then(
          (t) =>
            convertNullsToUndefined(t.data) as ProposalFormPartResponseAPI[],
        );
      proposalFormConfig.value = {
        parts: apiResponse.map(apiPartToClientPart),
      };
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
  }

  /*
  This filters out:
  - parts with no slots (empty part)
  - slots with no forms (empty slot)
  - parts with only empty slots

  The reason for this is that the API returns all parts and slots including empty ones for the sake of the proposal builder experience.
  However, for the other proposal contexts (preview, proposal submission), we don't want to show empty parts or empty slots to the end user.

  This is particularly useful when it comes to shortlist/proposal-specific forms.
  If a slot named "Shortlist" was added in the proposal builder, and a form specific to proposal A was added.
  When proposal B fetches the proposal form config, it will not get that proposal A specific form back in the proposal form config.
  This means that the slot that contained proposal A's form might be empty.
  We want to hide that slot to proposal B then, as there is no form to show for proposal B's owner.
  */
  const filteredProposalFormConfig = computed<ProposalFormConfig>(() => {
    if (!proposalFormConfig.value) return { parts: [] };
    const parts = [];

    for (const part of proposalFormConfig.value.parts) {
      if (part.slots.length > 0) {
        const nonEmptySlots = part.slots.filter(
          (slot) => slot.forms.length !== 0,
        );
        if (nonEmptySlots.length > 0) {
          parts.push({
            id: part.id,
            locationId: part.locationId,
            slots: nonEmptySlots,
          });
        }
        // If there are no slots that have forms, don't add this part
      }
    }

    return {
      parts: parts,
    };
  });

  const hasParts = computed(
    () => proposalFormConfig.value && proposalFormConfig.value.parts.length > 0,
  );
  async function createParts() {
    try {
      await client.post(`/rfps/${reactiveRfpId.value}/parts`);
    } catch (e) {
      error.value = e;
    } finally {
      await fetch();
      loading.value = false;
    }
  }

  async function createSlot(partId: string) {
    if (!partId) return;
    const requestBody = {
      slot_type: "any_of",
      label: "Slot",
    };
    try {
      await client.post(
        `/rfps/${reactiveRfpId.value}/parts/${partId}/slots`,
        requestBody,
      );
    } catch (e) {
      error.value = e;
    } finally {
      await fetch();
      loading.value = false;
    }
  }

  async function updateSlot(
    partId: string,
    slotId: string,
    slotType: string,
    label: string,
  ) {
    const requestBody = {
      slot_type: slotType,
      label: label,
    };
    try {
      await client.patch(
        `/rfps/${reactiveRfpId.value}/parts/${partId}/slots/${slotId}`,
        requestBody,
      );
    } catch (e) {
      error.value = e;
    } finally {
      loading.value = false;
    }
  }

  async function deleteSlot(partId: string, slotId: string) {
    if (!partId) return;
    try {
      await client.delete(
        `/rfps/${reactiveRfpId.value}/parts/${partId}/slots/${slotId}`,
      );
    } catch (e) {
      error.value = e;
    } finally {
      await fetch();
      loading.value = false;
    }
  }

  async function createForm(
    partId: string,
    slotId: string,
    formType: string,
    formLabel: string,
    settings?: FormSettings,
  ) {
    if (!partId) return;

    const requestBody = {
      proposal_form_type: formType,
      label: formLabel,
      analytics_tags: {},
      proposal_id: null,
      settings: settings,
    };
    try {
      await client.post(
        `/rfps/${reactiveRfpId.value}/parts/${partId}/slots/${slotId}/forms`,
        requestBody,
      );
    } catch (e) {
      error.value = e;
    } finally {
      await fetch();
      loading.value = false;
    }
  }

  async function updateForm(formId: string, requestBody: FormUpdateRequest) {
    try {
      await client.patch(
        `/rfps/${reactiveRfpId.value}/forms/${formId}`,
        requestBody,
      );
    } catch (e) {
      error.value = e;
    } finally {
      await fetch();
      loading.value = false;
    }
    return !error.value;
  }

  async function deleteForm(formId: string) {
    if (!formId) {
      error.value = "Form ID is required";
      return;
    }
    try {
      await client.delete(`/rfps/${reactiveRfpId.value}/forms/${formId}`, {
        params: {
          force: true,
        },
      });
    } catch (e) {
      error.value = e;
    } finally {
      await fetch();
      loading.value = false;
    }
  }

  function getCharLimit(question: Question) {
    if (question.char_limit) {
      return question.char_limit;
    } else if (question.question_type === "text") {
      return DEFAULT_TEXTFIELD_CHAR_LIMIT;
    } else if (question.question_type === "textarea") {
      return DEFAULT_TEXTAREA_CHAR_LIMIT;
    } else {
      return null;
    }
  }

  const downloading = ref(false);
  async function downloadFormCsv(proposalId?: string) {
    const { rfp } = useRfp(reactiveRfpId.value);
    try {
      pushToast({
        level: "info",
        message: "Downloading questions workbook",
      });
      downloading.value = true;

      const csvRows = [];

      // Constructing CSV here
      for (const part of filteredProposalFormConfig.value.parts) {
        // Get location name
        const { formatted, reloadLocation } = useRfpLocation(
          reactiveRfpId.value,
          part.locationId,
        );
        // Awaiting the reload function makes it so formatted.value is populated, since using the composable does not await the completion of the data fetching
        await reloadLocation();
        const locationText = formatted.value
          ? formatted.value
          : "All locations";

        for (const slot of part.slots) {
          for (const form of slot.forms) {
            // Make form link for provider to click back into the app from the CSV
            let formLink;
            if (proposalId) {
              formLink = `${env.appBaseUrl}/proposals/${proposalId}/forms/${form.id}`;
            } else {
              // By default, if there is no proposalId, go to the preview form
              formLink = `${env.appBaseUrl}/proposals/preview/forms/${form.id}?rfp_id=${reactiveRfpId.value}`;
            }

            if (form.type === "custom_form") {
              const { questions, reloadQuestions } = useCustomFormQuestions(
                reactiveRfpId.value,
                form.id,
              );
              // Awaiting the reload function makes it so questions.value is populated, since using the composable does not await the completion of the data fetching
              await reloadQuestions();
              if (questions.value) {
                for (const question of questions.value) {
                  let options: string[] = [];
                  if (question.options) {
                    options = question.options.map((option) => option["label"]);
                  }
                  const customQuestionRow = {
                    Location: locationText,
                    Section: slot.label,
                    Form: form.label,
                    Question: question.text,
                    Hint: question.hint,
                    Type: CustomQuestionTypeCSVLabel[question.question_type],
                    Required: question.required ? "Required" : "Optional",
                    Units: question.unit,
                    "Character limit": getCharLimit(question),
                    Options: options.join(", "),
                    "Form link": formLink,
                  };
                  csvRows.push(customQuestionRow);
                }
              }
            } else if (form.type === "timeline") {
              for (const label of Object.values(MilestoneTypeDict)) {
                const timelineMilestoneRow = {
                  Location: locationText,
                  Section: slot.label,
                  Form: form.label,
                  Question: label,
                  Hint: null,
                  Type: "Date",
                  Required: "Required",
                  Units: null,
                  "Character limit": null,
                  Options: null,
                  "Form link": formLink,
                };
                csvRows.push(timelineMilestoneRow);
              }
            } else if (form.type === "all_cash") {
              for (const label of Object.values(ItemCostCategoryDict)) {
                const costCategoryRow = {
                  Location: locationText,
                  Section: slot.label,
                  Form: form.label,
                  Question: label,
                  Hint: null,
                  Type: "Number",
                  Required: "Required",
                  Units: "USD",
                  "Character limit": null,
                  Options: null,
                  "Form link": formLink,
                };
                csvRows.push(costCategoryRow);
              }
              const otherDescRow = {
                Location: locationText,
                Section: slot.label,
                Form: form.label,
                Question: "Other Description",
                Hint: null,
                Type: "Text",
                Required: "Optional",
                Units: "",
                "Character limit": null,
                Options: null,
                "Form link": formLink,
              };
              csvRows.push(otherDescRow);
              const settings = form.settings;
              if (settings) {
                for (const [tech_type, presence_value] of objectEntries(
                  settings,
                )) {
                  if (presence_value === "required") {
                    const costCategoryRow = {
                      Location: locationText,
                      Section: slot.label,
                      Form: form.label,
                      Question:
                        TechCostCategoryDict[tech_type as TechCostCategory],
                      Hint: null,
                      Type: "Number",
                      Required: "Required",
                      Units: "USD",
                      "Character limit": null,
                      Options: null,
                      "Form link": formLink,
                    };
                    csvRows.push(costCategoryRow);
                  } else if (presence_value === "optional") {
                    const costCategoryRow = {
                      Location: locationText,
                      Section: slot.label,
                      Form: form.label,
                      Question:
                        TechCostCategoryDict[tech_type as TechCostCategory],
                      Hint: null,
                      Type: "Number",
                      Required: "Optional",
                      Units: "USD",
                      "Character limit": null,
                      Options: null,
                      "Form link": formLink,
                    };
                    csvRows.push(costCategoryRow);
                  }
                }
              }
            } else if (form.type === "ppa") {
              const settings = form.settings;
              if (settings) {
                for (const recOwnershipType of settings.rec_ownership_options) {
                  for (const term of settings.term_length_options) {
                    for (const escalator of settings.escalator_options) {
                      const recOwnershipText =
                        recOwnershipType === "buyer_retained"
                          ? "buyer retained"
                          : "provider retained";
                      const questionText = `PPA rate for a ${term}-year term PPA, assuming a ${tableValue(
                        escalator,
                        "%",
                      )} escalator, where the RECs produced are ${recOwnershipText}`;
                      const ppaRateRow = {
                        Location: locationText,
                        Section: slot.label,
                        Form: form.label,
                        Question: questionText,
                        Hint: null,
                        Type: "Number",
                        Required: "Optional",
                        Units: "USD/kWh",
                        "Character limit": null,
                        Options: null,
                        "Form link": formLink,
                      };
                      csvRows.push(ppaRateRow);
                    }
                  }
                }
              }
            }
          }
        }
      }
      const csvContents = d3.csvFormat(csvRows);
      const csvFilename = `[Workbook] ${
        rfp.value ? rfp.value.name : reactiveRfpId.value
      }.csv`;
      download(csvContents, csvFilename, "text/csv");
      pushToast({
        level: "success",
        message: "Successfully downloaded questions workbook",
      });
    } catch (e) {
      error.value = e;
      pushToast({
        level: "error",
        message: "Failed to download questions workbook",
      });
    } finally {
      downloading.value = false;
    }
  }

  async function cloneForm(
    partId: string,
    slotId: string,
    formIdToClone?: string,
  ) {
    if (!formIdToClone) return;
    try {
      await client.post(
        `/rfps/${reactiveRfpId.value}/parts/${partId}/slots/${slotId}/clone_form`,
        null,
        {
          params: {
            form_id_to_clone: formIdToClone,
          },
        },
      );
    } catch (e) {
      error.value = e;
      throw new Error(`Failed to clone form ID: ${formIdToClone}`);
    } finally {
      await fetch();
      loading.value = false;
    }
  }

  watch(reactiveRfpId, fetch, { immediate: true });

  return {
    loading,
    error,
    proposalFormConfig,
    filteredProposalFormConfig,
    reload: fetch,
    hasParts,
    createParts,
    createSlot,
    updateSlot,
    deleteSlot,
    createForm,
    updateForm,
    deleteForm,
    downloadFormCsv,
    downloading,
    cloneForm,
  };
}
